// ui/overlay.ts — the in-run modal: relic pick-1-of-N and the respite Altar.
//
// DOM, not canvas: these are menus, and the run is paused behind them (game.ts
// checks overlayOpen() before stepping the sim). Everything is keyboard-first
// (1/2/3 pick, E or Esc close) with click as the alternative, so the bot and the
// player drive the same surface.

import { clearMetaKeys } from '../engine/input'

export interface OverlayChoice {
  id: string
  name: string
  flavor: string
  /** plain mechanical statement of the effect, shown above the flavor (D-029) */
  desc?: string
  /** rarity or category label shown in the corner */
  tag: string
  /** tag colour — readability law: colour = class */
  color: string
  /** gold price; undefined = free */
  cost?: number
  disabled?: boolean
  note?: string
}

export interface OverlayOpts {
  domId: string
  title: string
  sub: string
  choices: OverlayChoice[]
  /** secondary row (altar consumables) */
  extras?: OverlayChoice[]
  gold?: number
  footer?: string
  /** null = the panel cannot be dismissed without choosing */
  closeLabel?: string | null
  /** return 'stay' to keep the panel open (e.g. after buying food) */
  onChoose(id: string): 'close' | 'stay'
  onClose?(): void
}

let root: HTMLElement | null = null
let panel: HTMLElement | null = null
let opts: OverlayOpts | null = null
let keyHandler: ((e: KeyboardEvent) => void) | null = null

function ui(): HTMLElement {
  if (!root) root = document.getElementById('ui') as HTMLElement
  return root
}

export function overlayOpen(): boolean {
  return panel != null
}

export function overlayId(): string {
  return panel?.id ?? ''
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

function cardHtml(c: OverlayChoice, key: number): string {
  const price = c.cost === undefined ? 'FREE' : `${c.cost}g`
  const cls = `ov-card${c.disabled ? ' is-off' : ''}`
  return `
    <button class="${cls}" data-choice="${esc(c.id)}" ${c.disabled ? 'disabled' : ''}>
      <span class="ov-key">${key}</span>
      <span class="ov-tag" style="color:${esc(c.color)}">${esc(c.tag)}</span>
      <span class="ov-name">${esc(c.name)}</span>
      ${c.desc ? `<span class="ov-desc">${esc(c.desc)}</span>` : ''}
      <span class="ov-flavor">${esc(c.flavor)}</span>
      <span class="ov-price">${esc(price)}${c.note ? ` · ${esc(c.note)}` : ''}</span>
    </button>`
}

function render(): void {
  if (!panel || !opts) return
  const o = opts
  let n = 0
  const cards = o.choices.map((c) => cardHtml(c, ++n)).join('')
  const extras = (o.extras ?? []).map((c) => cardHtml(c, ++n)).join('')
  panel.innerHTML = `
    <h2 class="ov-title">${esc(o.title)}</h2>
    <p class="ov-sub">${esc(o.sub)}</p>
    ${o.gold === undefined ? '' : `<div class="ov-gold">GOLD <b>${o.gold}</b></div>`}
    <div class="ov-cards">${cards}</div>
    ${extras ? `<div class="ov-cards ov-extras">${extras}</div>` : ''}
    ${o.footer ? `<div class="ov-foot">${esc(o.footer)}</div>` : ''}
    ${o.closeLabel === null ? '' : `<button class="btn ov-close">${esc(o.closeLabel ?? 'LEAVE  (E)')}</button>`}
  `
  const cardEls = panel.querySelectorAll('[data-choice]')
  cardEls.forEach((el) => {
    el.addEventListener('click', () => {
      const id = (el as HTMLElement).dataset.choice
      if (id) choose(id)
    })
  })
  const closeEl = panel.querySelector('.ov-close')
  closeEl?.addEventListener('click', () => closeOverlay())
}

function choose(id: string): void {
  if (!opts) return
  const all = [...opts.choices, ...(opts.extras ?? [])]
  const c = all.find((x) => x.id === id)
  if (!c || c.disabled) return
  const verdict = opts.onChoose(id)
  if (verdict === 'close') closeOverlay()
  else render()
}

function chooseByIndex(i: number): void {
  if (!opts) return
  const all = [...opts.choices, ...(opts.extras ?? [])]
  const c = all[i]
  if (c) choose(c.id)
}

export function showOverlay(o: OverlayOpts): void {
  closeOverlay()
  opts = o
  const el = document.createElement('div')
  el.className = 'screen overlay'
  el.id = o.domId
  ui().appendChild(el)
  panel = el
  render()

  keyHandler = (e: KeyboardEvent): void => {
    if (!panel) return
    if (e.code === 'Escape' || e.code === 'KeyE') {
      if (o.closeLabel !== null) {
        e.preventDefault()
        closeOverlay()
        // Eat the edge: the same Escape press must not also open the pause
        // menu on the next frame (game.ts polls metaKeyPressed('Escape')).
        clearMetaKeys()
      }
      return
    }
    const m = /^(?:Digit|Numpad)([1-9])$/.exec(e.code)
    if (m) {
      e.preventDefault()
      chooseByIndex(Number(m[1]) - 1)
    }
  }
  document.addEventListener('keydown', keyHandler)
}

/** Re-render with fresh gold/choice state (used after an altar purchase). */
export function updateOverlay(patch: Partial<Pick<OverlayOpts, 'gold' | 'choices' | 'extras' | 'sub' | 'footer'>>): void {
  if (!opts) return
  if (patch.gold !== undefined) opts.gold = patch.gold
  if (patch.choices) opts.choices = patch.choices
  if (patch.extras) opts.extras = patch.extras
  if (patch.sub !== undefined) opts.sub = patch.sub
  if (patch.footer !== undefined) opts.footer = patch.footer
  render()
}

export function closeOverlay(): void {
  if (keyHandler) {
    document.removeEventListener('keydown', keyHandler)
    keyHandler = null
  }
  const o = opts
  if (panel && panel.parentElement) panel.parentElement.removeChild(panel)
  panel = null
  opts = null
  o?.onClose?.()
}
